// src/pages/admin/AdminAuditLogs.jsx — Phase 5.3
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Typography, Button, Table, TableHead,
  TableRow, TableCell, TableBody, CircularProgress, Select,
  MenuItem, FormControl, InputLabel, Chip, TextField, alpha,
  Tooltip, IconButton,
} from "@mui/material";
import DownloadIcon  from "@mui/icons-material/Download";
import RefreshIcon   from "@mui/icons-material/Refresh";
import HistoryIcon   from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import API from "../../api";
import { useToast } from "../../context/ToastContext";
import { C, gradientText } from "../../theme";
import PaginationBar from "../../components/PaginationBar";

// ── Catégories d'actions pour le filtre rapide ──────────────────────────────
const ACTION_GROUPS = [
  { value: "",        label: "Toutes les actions" },
  { value: "auth.",   label: "Authentification" },
  { value: "user.",   label: "Données personnelles" },
  { value: "admin.",  label: "Actions administrateur" },
];

// ── Libellés et couleurs par action ─────────────────────────────────────────
const ACTION_META = {
  "auth.login.success":          { label: "Connexion réussie",          color: C.teal[600] },
  "auth.login.failed":           { label: "Connexion échouée",          color: "#c0392b" },
  "auth.logout":                 { label: "Déconnexion",                color: C.slate[500] },
  "auth.register":               { label: "Inscription",                color: C.indigo[600] },
  "auth.password.reset_requested": { label: "Demande reset mot de passe", color: "#d97706" },
  "auth.password.reset_completed": { label: "Mot de passe réinitialisé",  color: C.teal[600] },
  "user.data.export":            { label: "Export de données (RGPD)",   color: C.indigo[600] },
  "admin.user.update":           { label: "Modification utilisateur",   color: "#d97706" },
  "admin.user.delete":           { label: "Suppression utilisateur",    color: "#c0392b" },
};

function actionMeta(action) {
  return ACTION_META[action] || { label: action, color: C.slate[600] };
}

export default function AdminAuditLogs() {
  const toast = useToast();
  const [items,       setItems]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [pages,       setPages]       = useState(1);
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(50);
  const [actionFilter, setActionFilter] = useState("");
  const [actorIdFilter, setActorIdFilter] = useState("");
  const [loading,     setLoading]     = useState(false);
  const [exporting,   setExporting]   = useState(false);

  useEffect(() => { document.title = "Audit RGPD — Admin ExamOnline"; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, page_size: pageSize });
      if (actionFilter)   p.set("action", actionFilter);
      if (actorIdFilter)  p.set("actor_id", actorIdFilter);
      const res = await API.get(`/admin/audit-logs/?${p}`);
      const data = res.data;
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch {
      toast.error("Impossible de charger les logs d'audit.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, actionFilter, actorIdFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [actionFilter, actorIdFilter]);

  const formatDate = iso => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("fr-FR", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
    } catch { return iso; }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams({ limit: 5000 });
      if (actionFilter)  p.set("action", actionFilter);
      if (actorIdFilter) p.set("actor_id", actorIdFilter);
      const res = await API.get(`/admin/audit-logs/export?${p}`);
      const logs = res.data.items || [];

      const rows = [
        ["ID", "Date", "Action", "Acteur ID", "Acteur", "Cible ID", "Cible", "Détail", "IP", "User-Agent"],
        ...logs.map(l => [
          l.id, formatDate(l.created_at), l.action,
          l.actor_id ?? "", l.actor_username ?? "",
          l.target_id ?? "", l.target_username ?? "",
          l.detail ?? "", l.ip_address ?? "", l.user_agent ?? "",
        ]),
      ];
      const csv = rows.map(r =>
        r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")
      ).join("\n");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
      a.download = `audit_logs_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      toast.success(`${logs.length} log(s) exporté(s).`);
    } catch {
      toast.error("Export impossible.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            <Box component="span" sx={gradientText}>Audit RGPD</Box>
          </Typography>
          <Typography color="text.secondary" sx={{ mt: .5, fontSize: "0.9rem" }}>
            Traçabilité des accès et modifications de données personnelles · rétention 1 an
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Actualiser
          </Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} disabled={exporting}>
            {exporting ? "Export..." : "Exporter CSV"}
          </Button>
        </Stack>
      </Stack>

      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: "12px" }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Catégorie</InputLabel>
            <Select
              value={actionFilter}
              label="Catégorie"
              onChange={e => setActionFilter(e.target.value)}
            >
              {ACTION_GROUPS.map(g => (
                <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="ID utilisateur (acteur)"
            value={actorIdFilter}
            onChange={e => setActorIdFilter(e.target.value.replace(/\D/g, ""))}
            sx={{ width: 200 }}
          />
          <Tooltip title="Les logs sont automatiquement purgés après 1 an, conformément au principe de minimisation des données (Art. 5.1.e RGPD).">
            <Stack direction="row" spacing={.5} alignItems="center" sx={{ color: "text.secondary" }}>
              <InfoOutlinedIcon fontSize="small" />
              <Typography variant="caption">Rétention : 1 an</Typography>
            </Stack>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: "12px", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <HistoryIcon sx={{ fontSize: 44, color: C.slate[300], mb: 1.5 }} />
            <Typography color="text.secondary" fontWeight={600}>
              Aucun log d'audit pour ces filtres.
            </Typography>
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: C.slate[50] }}>
                  {["Date", "Action", "Acteur", "Cible", "Détail", "IP"].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem",
                      textTransform: "uppercase", letterSpacing: "0.04em", color: C.slate[600] }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(log => {
                  const meta = actionMeta(log.action);
                  return (
                    <TableRow key={log.id} sx={{ "&:hover": { background: C.slate[50] } }}>
                      <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.8rem", color: C.slate[600] }}>
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={meta.label}
                          size="small"
                          sx={{
                            fontWeight: 700, fontSize: "0.72rem",
                            background: alpha(meta.color, .1), color: meta.color,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>
                        {log.actor_username
                          ? <>{log.actor_username} <Typography component="span" variant="caption" color="text.secondary">#{log.actor_id}</Typography></>
                          : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>
                        {log.target_username
                          ? <>{log.target_username} <Typography component="span" variant="caption" color="text.secondary">#{log.target_id}</Typography></>
                          : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.78rem", color: C.slate[600], maxWidth: 240 }}>
                        <Tooltip title={log.detail || ""}>
                          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {log.detail || "—"}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.78rem", color: C.slate[500], fontFamily: "monospace" }}>
                        {log.ip_address || "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Box sx={{ px: 2, pb: 1 }}>
              <PaginationBar
                page={page} pages={pages} total={total} pageSize={pageSize}
                onPageChange={setPage} onPageSizeChange={setPageSize}
                pageSizeOptions={[25, 50, 100]} loading={loading}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}